import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { getUser } from "../../../../utils/commonfunction";
import { SOCKET_EVENTS, ROLE } from "../../../../utils/Constants";
import { SOCKET_CLIENT } from "../../../../utils/socket.js";
import { acceptProduct, deleteLiveStreamProduct, undoAcceptProduct, getStreamProducts, updateLiveStreamProduct, getStreamDetails, addProductToStream, getColorPalettes } from "../../../../modules/user/services/stream.service";
import Swal from "sweetalert2";
import Axios from "../../../../utils/axios";
import { useFormik } from "formik";
import * as Yup from "yup";
import StaffComments from "./StaffComments";
import CategorySelectDropdown from "../../components/CategorySelectDropdown";
import { ZegoExpressEngine } from "zego-express-engine-webrtc";
import { GetStreamToken } from "../../../../modules/advertiser/services/cms.service";
import { X, Camera, Plus, Send, Pencil } from "lucide-react";
import { GetVertexAi } from "../../../../modules/advertiser/services/vertex.services";
import { GetCategoryList } from "../../../../modules/advertiser/services/category.service";
import { GetSubCategoryList } from "../../../../modules/advertiser/services/subcategory.service";
import { GetPreSubCategoryList, GetPreSubCategoryDetails } from "../../../../modules/advertiser/services/presubcategory.service";
import { AnalyzeProductImage } from "../../../../modules/advertiser/services/prodcut.service";
import { METALLIC_COLORS, SPECIAL_COLORS, WHITE_SHADES } from "../../../admin/common/colorPalettePicker";
import ProductAttributesSection from "../../components/ProductAttributesSection";

const appID = Number(process.env.REACT_APP_ZEGO_APP_ID);
const server = process.env.REACT_APP_ZEGO_server;
export default function StaffLiveStream() {
    const { streamId } = useParams();
    const navigate = useNavigate();
    const location = useLocation();

    // Video Player & Stream Meta State
    const [streamMeta, setStreamMeta] = useState(null);
    const isPast = location.state?.isPast === true || streamMeta?.autoStatus === "past" || streamMeta?.isStreamCompleted === true;
    const isUpcoming = !isPast && (location.state?.isUpcoming === true || streamMeta?.autoStatus === "upcoming" || (streamMeta && !streamMeta.isLive && !streamMeta.isStreamCompleted));

    const [loading, setLoading] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);
    const [productsByTab, setProductsByTab] = useState({
        accepted: [],
        not_accepted: [],
        existing: [],
    });
    const [tabCounts, setTabCounts] = useState({
        accepted: 0,
        not_accepted: 0,
        existing: 0,
    });
    const [pages, setPages] = useState({
        accepted: 1,
        not_accepted: 1,
        existing: 1,
    });
    const [hasMore, setHasMore] = useState({
        accepted: true,
        not_accepted: true,
        existing: true,
    });
    const [activeTab, setActiveTab] = useState("accepted");
    const observerTarget = useRef(null);

    const [isViewing, setIsViewing] = useState(false);
    const [engine, setEngine] = useState(null);
    const [totalViewers, setTotalViewers] = useState(0);
    const remoteVideoRef = useRef(null);
    const canvasRef = useRef(null);
    const [editMode, setEditMode] = useState(false);
    const [editProductId, setEditProductId] = useState(null);
    const productCacheRef = useRef(new Map());
    const [existingImage, setExistingImage] = useState(null);
    const [imagePreview, setImagePreview] = useState(null);
    const [showCommentsSidebar, setShowCommentsSidebar] = useState(false);

    let user = getUser(ROLE.USER);
    let isAdvertiser = false;
    if (user && user.token) {
        if (user.isAdvertiser) {
            isAdvertiser = true;
        }
    } else {
        user = getUser(ROLE.ADVERTISER);
        if (user && user.token) {
            isAdvertiser = true;
        }
    }
    const userId = user?._id;
    // Priority to permissions passed from AssignedStreams.js
    const rawPermissions = location.state?.permissions || user?.permissions || [];
    const permissions = typeof rawPermissions === 'string' ? (rawPermissions.startsWith('[') ? JSON.parse(rawPermissions) : [rawPermissions]) : (rawPermissions || []);

    const canAccept = isAdvertiser || permissions.includes("accept_product");
    const canEdit = isAdvertiser || permissions.includes("edit_product");
    const canDelete = isAdvertiser || permissions.includes("delete_product");
    const canReplyOnComments = isAdvertiser || permissions.includes("reply_on_comments");

    const fetchStreamMeta = async () => {
        try {
            const resp = await getStreamDetails(streamId);
            if (resp.success && resp.data) {
                setStreamMeta(resp.data);
                const viewerCount = resp.data.listOfViewers?.length || 0;
                setTotalViewers(viewerCount);
            }
        } catch (error) {
            console.error("Failed to fetch stream meta:", error);
        }
    };

    const startViewing = async () => {
        try {
            if (!streamMeta || streamMeta.isLive === false) return;
            const targetRoomId = streamMeta.room_id || streamMeta.roomID || streamId;
            const zg = new ZegoExpressEngine(appID, server);
            setEngine(zg);
            const uniqueUserId = userId ? `${userId}_staff` : `staff_${Date.now()}`;
            const res = await GetStreamToken(uniqueUserId, targetRoomId);
            const token = res.token;
            const finalRoomId = res.roomID || targetRoomId;
            const finalUserId = res.userID || uniqueUserId;
            const userName = "staff_" + (user?.name || userId || "user");

            zg.on("roomStreamUpdate", async (roomID, updateType, streamList) => {
                if (updateType === "ADD" && streamList && streamList.length > 0) {
                    try {
                        const targetStream = streamList[0];
                        const remoteStream = await zg.startPlayingStream(targetStream.streamID);
                        if (remoteVideoRef.current) {
                            remoteVideoRef.current.srcObject = remoteStream;
                            remoteVideoRef.current.muted = true;
                            await remoteVideoRef.current.play().catch((e) => console.warn("Video play error:", e));
                        }
                    } catch (playErr) {
                        console.error("Error playing added stream:", playErr);
                    }
                }
            });

            await zg.loginRoom(finalRoomId, token, { userID: finalUserId, userName });

            const zegoStreamId = `stream_${streamId}`;
            try {
                const remoteStream = await zg.startPlayingStream(zegoStreamId);
                if (remoteVideoRef.current) {
                    remoteVideoRef.current.srcObject = remoteStream;
                    remoteVideoRef.current.muted = true;
                    await remoteVideoRef.current.play().catch((e) => console.warn("Video play error:", e));
                }
                setIsViewing(true);
            } catch (playErr) {
                console.warn("Direct stream play waiting for stream:", playErr);
                try {
                    if (typeof zg.getRoomStreamList === "function") {
                        const list = await zg.getRoomStreamList(finalRoomId);
                        if (list && list.length > 0) {
                            const remoteStream = await zg.startPlayingStream(list[0].streamID);
                            if (remoteVideoRef.current) {
                                remoteVideoRef.current.srcObject = remoteStream;
                                remoteVideoRef.current.muted = true;
                                await remoteVideoRef.current.play().catch((e) => console.warn("Video play error:", e));
                            }
                            setIsViewing(true);
                        }
                    }
                } catch (listErr) {
                    console.error("getRoomStreamList error:", listErr);
                }
            }
        } catch (error) {
            console.error("Error starting viewer:", error);
        }
    };

    const stopViewing = async () => {
        try {
            if (engine) {
                const targetRoomId = streamMeta?.room_id || streamMeta?.roomID || streamId;
                try {
                    await engine.stopPlayingStream(`stream_${streamId}`);
                } catch (_) {}
                try {
                    await engine.logoutRoom(targetRoomId);
                } catch (_) {}
                engine.destroyEngine();
                setEngine(null);
            }
            if (remoteVideoRef.current && remoteVideoRef.current.srcObject) {
                remoteVideoRef.current.srcObject.getTracks().forEach(track => track.stop());
                remoteVideoRef.current.srcObject = null;
            }
            setIsViewing(false);
        } catch (error) {
            console.error("Error stopping viewer:", error);
        }
    };

    const captureScreenshot = () => {
        const video = remoteVideoRef.current;
        const canvas = canvasRef.current;
        if (!video || !canvas || video.videoWidth === 0 || video.videoHeight === 0) return;

        if ((streamMeta?.remaining_ai_clicks ?? streamMeta?.seller?.remaining_ai_clicks ?? 0) <= 0) {
            Swal.fire({
                icon: "warning",
                title: "Insufficient AI Clicks",
                text: "Insufficient AI clicks. Ask seller to purchase more.",
                toast: true,
                position: "top-end",
                timer: 4000,
                showConfirmButton: false,
            });
            return;
        }

        const ctx = canvas.getContext("2d");
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

        const base64Image = canvas.toDataURL("image/jpeg", 0.7);

        sendFramesToBackend([base64Image]);
        Swal.fire({
            icon: "success",
            title: "Screenshot Sent",
            text: "Frame sent to AI successfully.",
            toast: true,
            position: "top-end",
            timer: 3000,
            showConfirmButton: false,
        });
    };

    const sendFramesToBackend = async (frames) => {
        try {
            await GetVertexAi({
                frames,
                seller_id: streamMeta?.seller?._id || streamMeta?.seller_id || userId,
                stream_id: streamId,
            });
        } catch (error) {
            console.error("Error sending frames:", error);
        }
    };

    useEffect(() => {
        fetchStreamMeta();
    }, [streamId]);

    useEffect(() => {
        if (streamMeta && streamMeta.isLive && !engine) {
            startViewing();
        }
    }, [streamId, streamMeta?.isLive, engine]);

    // Separate effect for cleanup on unmount or streamId change
    useEffect(() => {
        return () => {
            stopViewing();
        };
    }, [streamId]);

    const fetchProductsForTab = async (tabToFetch, pageNum = 1, isInitial = false, showLoader = false) => {
        try {
            if (showLoader) setLoading(true);
            if (pageNum > 1) setLoadingMore(true);

            const payload = {
                stream_id: streamId,
                tab: isInitial ? "all" : tabToFetch,
                page: pageNum,
                limit: 12
            };

            const response = await Axios({ role: ROLE.USER }).post(`advertiser/streams/get-stream-products`, payload);

            if (response.success && response.data) {
                const data = response.data;
                if (data && typeof data === 'object' && !Array.isArray(data)) {
                    if (data.counts) {
                        setTabCounts(data.counts);
                    }

                    setProductsByTab(prev => {
                        if (isInitial || payload.tab === "all") {
                            return {
                                accepted: data.accepted || [],
                                not_accepted: data.not_accepted || [],
                                existing: data.existing || [],
                            };
                        }

                        if (pageNum === 1) {
                            return {
                                ...prev,
                                [tabToFetch]: data[tabToFetch] || prev[tabToFetch] || [],
                                ...(data.accepted && tabToFetch !== "accepted" && (!prev.accepted || prev.accepted.length === 0) ? { accepted: data.accepted } : {}),
                                ...(data.not_accepted && tabToFetch !== "not_accepted" && (!prev.not_accepted || prev.not_accepted.length === 0) ? { not_accepted: data.not_accepted } : {}),
                                ...(data.existing && tabToFetch !== "existing" && (!prev.existing || prev.existing.length === 0) ? { existing: data.existing } : {}),
                            };
                        } else {
                            const newItems = data[tabToFetch] || [];
                            const existingItems = prev[tabToFetch] || [];
                            const existingIds = new Set(existingItems.map(p => String(p._id)));
                            const filteredNew = newItems.filter(p => !existingIds.has(String(p._id)));
                            return {
                                ...prev,
                                [tabToFetch]: [...existingItems, ...filteredNew]
                            };
                        }
                    });

                    setHasMore(prev => ({
                        ...prev,
                        [tabToFetch]: Boolean(data.pagination?.hasMore)
                    }));

                    setPages(prev => ({
                        ...prev,
                        [tabToFetch]: pageNum
                    }));
                }
            }
        } catch (error) {
            console.error("Error fetching products:", error);
        } finally {
            if (showLoader) setLoading(false);
            setLoadingMore(false);
        }
    };

    const fetchInitialProducts = async (showLoading = true) => {
        setPages({ accepted: 1, not_accepted: 1, existing: 1 });
        setHasMore({ accepted: true, not_accepted: true, existing: true });
        await fetchProductsForTab(activeTab, 1, true, showLoading);
    };

    const handleTabChange = (newTab) => {
        setActiveTab(newTab);
        if ((!productsByTab[newTab] || productsByTab[newTab].length === 0) && tabCounts[newTab] > 0) {
            fetchProductsForTab(newTab, 1, false, true);
        }
    };

    useEffect(() => {
        const target = observerTarget.current;
        if (!target) return;

        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting && hasMore[activeTab] && !loading && !loadingMore) {
                    const nextPage = (pages[activeTab] || 1) + 1;
                    fetchProductsForTab(activeTab, nextPage, false, false);
                }
            },
            { threshold: 0.1, rootMargin: "100px" }
        );

        observer.observe(target);

        return () => {
            observer.disconnect();
        };
    }, [activeTab, hasMore, loading, loadingMore, pages]);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [isAddModalOpen, setIsAddModalOpen] = useState(false);
    const [editProductIsLive, setEditProductIsLive] = useState(false);
    const [editSubCategoryList, setEditSubCategoryList] = useState([]);
    const [editPreSubCategoryList, setEditPreSubCategoryList] = useState([]);
    const [isEditSubCategoryLoading, setIsEditSubCategoryLoading] = useState(false);
    const [isEditPreSubCategoryLoading, setIsEditPreSubCategoryLoading] = useState(false);
    const [isEditAnalyzing, setIsEditAnalyzing] = useState(false);
    const [isSubmittingEdit, setIsSubmittingEdit] = useState(false);
    const [colorPickerTarget, setColorPickerTarget] = useState("add");

    const editValidationSchema = Yup.object({
        title: Yup.string()
            .trim()
            .required("Title is required")
            .min(2, "Title must be at least 2 characters"),

        price: Yup.mixed().when("isAskForPrice", {
            is: false,
            then: () => Yup.number()
                .required("Price is required")
                .typeError("Price must be a valid number")
                .positive("Price must be positive")
                .max(10000000, "Price too large"),
            otherwise: () => Yup.mixed().notRequired(),
        }),

        description: Yup.string().trim(),

        colors: Yup.array()
            .min(1, "At least one color is required")
            .required("Color is required"),

        categoryId: Yup.string()
            .trim()
            .required("Category is required"),

        subcategoryId: Yup.string()
            .trim()
            .required("Sub category is required"),

        preSubCategoryId: Yup.string()
            .trim()
            .required("Pre-sub category is required"),
    });

    const handleCloseModal = () => {
        formik.resetForm();
        setEditMode(false);
        setEditProductId(null);
        setEditProductIsLive(false);
        setExistingImage(null);
        setImagePreview(null);
        setEditSubCategoryList([]);
        setEditPreSubCategoryList([]);
        setEditDynamicAttributes([]);
        setIsColorPickerOpen(false);
        setIsEditModalOpen(false);
    };

    const formik = useFormik({
        initialValues: {
            title: "",
            description: "",
            price: "",
            isAskForPrice: false,
            colors: [],
            picture: null,
            categoryId: "",
            subcategoryId: "",
            preSubCategoryId: "",
            attributes: [],
            useAI: true,
        },
        validationSchema: editValidationSchema,
        onSubmit: () => {}
    });

    const handleEditImageChange = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        formik.setFieldValue("picture", file);

        const reader = new FileReader();
        reader.onloadend = async () => {
            setImagePreview(reader.result);

            if (reader.result && formik.values.useAI) {
                const clicks = balanceData?.remaining_ai_clicks ?? 0;
                if (clicks <= 0) {
                    Swal.fire({
                        icon: "warning",
                        title: "Insufficient AI Clicks",
                        text: "You have 0 AI clicks remaining.",
                    });
                    formik.setFieldValue("useAI", false);
                    return;
                }

                setIsEditAnalyzing(true);
                try {
                    const resp = await AnalyzeProductImage(reader.result);
                    if (resp && resp.success && resp.data) {
                        if (!formik.values.title && resp.data.title) {
                            formik.setFieldValue("title", resp.data.title);
                        }
                        if (!formik.values.description && resp.data.description) {
                            formik.setFieldValue("description", resp.data.description);
                        }
                        if (resp.data.remaining_ai_clicks !== undefined) {
                            setBalanceData(prev => ({ ...prev, remaining_ai_clicks: resp.data.remaining_ai_clicks }));
                        }
                    } else if (resp && resp.data && resp.data.remaining_ai_clicks !== undefined) {
                        setBalanceData(prev => ({ ...prev, remaining_ai_clicks: resp.data.remaining_ai_clicks }));
                    }
                } catch (err) {
                    console.error("AI analysis error:", err);
                    if (err?.response?.data?.data?.remaining_ai_clicks !== undefined) {
                        setBalanceData(prev => ({ ...prev, remaining_ai_clicks: err.response.data.data.remaining_ai_clicks }));
                    }
                } finally {
                    setIsEditAnalyzing(false);
                }
            }
        };
        reader.readAsDataURL(file);
    };

    const handleEditCategoryChange = (catId) => {
        formik.setFieldValue("categoryId", catId);
        formik.setFieldTouched("categoryId", true, true);
        formik.setFieldValue("subcategoryId", "");
        formik.setFieldValue("preSubCategoryId", "");
        formik.setFieldValue("attributes", []);
        setEditSubCategoryList([]);
        setEditPreSubCategoryList([]);
        setEditDynamicAttributes([]);

        if (catId) {
            setIsEditSubCategoryLoading(true);
            GetSubCategoryList({ categoryId: catId })
                .then((res) => {
                    const list = res?.list || res?.data?.list || [];
                    setEditSubCategoryList(list);
                })
                .catch((err) => console.error("Error loading subcategories:", err))
                .finally(() => setIsEditSubCategoryLoading(false));
        }
    };

    const handleEditSubCategoryChange = (subId) => {
        formik.setFieldValue("subcategoryId", subId);
        formik.setFieldValue("preSubCategoryId", "");
        formik.setFieldValue("attributes", []);
        setEditPreSubCategoryList([]);
        setEditDynamicAttributes([]);

        if (subId) {
            setIsEditPreSubCategoryLoading(true);
            GetPreSubCategoryList({ subcategoryId: subId })
                .then((res) => {
                    const list = res?.list || res?.data?.list || [];
                    setEditPreSubCategoryList(list);
                })
                .catch((err) => console.error("Error loading pre-subcategories:", err))
                .finally(() => setIsEditPreSubCategoryLoading(false));
        }
    };

    const handleEditPreSubCategoryChange = (val) => {
        formik.setFieldValue("preSubCategoryId", val);
        formik.setFieldTouched("preSubCategoryId", true, true);
        if (val) {
            setIsEditAttributeLoading(true);
            GetPreSubCategoryDetails(val)
                .then((res) => {
                    const responseData = res?.data || res;
                    const attributes = responseData?.data?.attributes || responseData?.attributes;
                    if (attributes && Array.isArray(attributes)) {
                        setEditDynamicAttributes(attributes);
                        const currentFormAttrs = Array.isArray(formik.values.attributes) ? [...formik.values.attributes] : [];
                        const updatedAttrs = attributes.map(attr => {
                            const existing = currentFormAttrs.find(a => a && a.name === attr.name);
                            return {
                                name: attr.name,
                                value: existing ? existing.value : (attr.isMultiSelect || attr.attributeType === "checkbox" ? [] : "")
                            };
                        });
                        formik.setFieldValue("attributes", updatedAttrs);
                    } else {
                        setEditDynamicAttributes([]);
                    }
                })
                .catch((err) => {
                    console.error("Attribute fetch error:", err);
                    setEditDynamicAttributes([]);
                })
                .finally(() => setIsEditAttributeLoading(false));
        } else {
            setEditDynamicAttributes([]);
            formik.setFieldValue("attributes", []);
        }
    };

    const handleUpdateProduct = async (isPublished) => {
        const errors = await formik.validateForm();
        formik.setTouched({
            title: true,
            price: true,
            picture: true,
            colors: true,
            categoryId: true,
            subcategoryId: true,
            preSubCategoryId: true,
        });

        const hasColorError = !formik.values.colors || formik.values.colors.length === 0;
        const hasCategoryError = !formik.values.categoryId;
        const hasSubCategoryError = !formik.values.subcategoryId;
        const hasPreSubCategoryError = !formik.values.preSubCategoryId;

        if (Object.keys(errors).length > 0 || hasColorError || hasCategoryError || hasSubCategoryError || hasPreSubCategoryError) {
            if (hasColorError) {
                formik.setFieldError("colors", "At least one color is required");
            }
            if (hasCategoryError) {
                formik.setFieldError("categoryId", "Category is required");
            }
            if (hasSubCategoryError) {
                formik.setFieldError("subcategoryId", "Sub category is required");
            }
            if (hasPreSubCategoryError) {
                formik.setFieldError("preSubCategoryId", "Pre-sub category is required");
            }
            return;
        }

        if (!imagePreview && !formik.values.picture && !existingImage) {
            Swal.fire({
                icon: "error",
                text: "Product picture is required",
            });
            return;
        }

        try {
            setIsSubmittingEdit(true);
            const formData = new FormData();
            formData.append("_id", editProductId);
            formData.append("stream_id", streamId);
            formData.append("title", formik.values.title);
            formData.append("description", formik.values.description || "");
            formData.append("isAskForPrice", formik.values.isAskForPrice ? "true" : "false");
            formData.append("price", formik.values.isAskForPrice ? "0" : (formik.values.price || "0"));
            if (formik.values.colors && formik.values.colors.length > 0) {
                formData.append("colors", JSON.stringify(formik.values.colors));
            } else {
                formData.append("colors", JSON.stringify([]));
            }
            if (formik.values.categoryId) {
                formData.append("categoryId", formik.values.categoryId);
            }
            if (formik.values.subcategoryId) {
                formData.append("subcategoryId", formik.values.subcategoryId);
            } else {
                formData.append("subcategoryId", "");
            }
            if (formik.values.preSubCategoryId) {
                formData.append("preSubCategoryId", formik.values.preSubCategoryId);
            } else {
                formData.append("preSubCategoryId", "");
            }

            const attributesObj = {};
            if (formik.values.colors && formik.values.colors.length > 0) {
                attributesObj["Colors"] = formik.values.colors;
            }
            if (Array.isArray(formik.values.attributes)) {
                formik.values.attributes.forEach(attr => {
                    if (attr && attr.name && attr.value !== undefined && attr.value !== "" && (!Array.isArray(attr.value) || attr.value.length > 0)) {
                        attributesObj[attr.name] = attr.value;
                    }
                });
            }
            if (Object.keys(attributesObj).length > 0) {
                formData.append("attributes", JSON.stringify(attributesObj));
            }
            if (isPublished !== undefined) {
                formData.append("isPublished", isPublished ? "true" : "false");
            }

            if (formik.values.picture) {
                formData.append("image", formik.values.picture);
            } else if (existingImage) {
                formData.append("image", existingImage);
            }

            const response = await updateLiveStreamProduct(formData);

            if (response.success) {
                handleCloseModal();
                await fetchInitialProducts(false);
                if (isPublished) {
                    setActiveTab("accepted");
                }
                Swal.fire({
                    icon: "success",
                    title: isPublished ? "Product Published" : "Product Updated",
                    text: isPublished ? "Product updated and published to stream viewers!" : "Product updated successfully!",
                    timer: 2000,
                    showConfirmButton: false,
                });
            } else {
                Swal.fire({
                    icon: "error",
                    text: response?.message || "Failed to update product",
                });
            }
        } catch (error) {
            console.error("Update product error:", error);
            Swal.fire({
                icon: "error",
                text: error?.response?.data?.message || error?.message || "Failed to update product",
            });
        } finally {
            setIsSubmittingEdit(false);
        }
    };

    // Advertiser-style Create Product States
    const [isSubmittingAdd, setIsSubmittingAdd] = useState(false);
    const [addImagePreview, setAddImagePreview] = useState(null);
    const [isAnalyzing, setIsAnalyzing] = useState(false);
    const [categoryList, setCategoryList] = useState([]);
    const [subCategoryList, setSubCategoryList] = useState([]);
    const [preSubCategoryList, setPreSubCategoryList] = useState([]);
    const [isSubCategoryLoading, setIsSubCategoryLoading] = useState(false);
    const [isPreSubCategoryLoading, setIsPreSubCategoryLoading] = useState(false);
    const [addDynamicAttributes, setAddDynamicAttributes] = useState([]);
    const [isAddAttributeLoading, setIsAddAttributeLoading] = useState(false);
    const [editDynamicAttributes, setEditDynamicAttributes] = useState([]);
    const [isEditAttributeLoading, setIsEditAttributeLoading] = useState(false);
    const [colorPalettes, setColorPalettes] = useState([]);
    const [isColorPickerOpen, setIsColorPickerOpen] = useState(false);
    const [balanceData, setBalanceData] = useState({ remaining_ai_clicks: 20 });

    const getSwatchColorCode = (colorName) => {
        if (!colorName) return "#ccc";
        const lowerName = colorName.toLowerCase().trim();
        const metallic = (METALLIC_COLORS || []).find(m => m.name.toLowerCase() === lowerName);
        if (metallic) return metallic.gradient || metallic.hex;
        const special = (SPECIAL_COLORS || []).find(s => s.name.toLowerCase() === lowerName);
        if (special) {
            if (lowerName === 'transparent') return 'transparent';
            if (lowerName === 'multicolour' || lowerName === 'multicolor') return 'multicolour';
            return special.hex;
        }
        const white = (WHITE_SHADES || []).find(w => w.name.toLowerCase() === lowerName);
        if (white) return white.hex;
        for (const col of colorPalettes) {
            if (col.colors) {
                const found = col.colors.find(c => c.name.toLowerCase() === lowerName);
                if (found) return found.hex;
            }
        }
        return "#ccc";
    };

    const getPalateBackground = (colors) => {
        if (!colors || colors.length === 0) {
            return "linear-gradient(135deg, #ff0000, #ff7f00, #ffff00, #00ff00, #0000ff, #4b0082, #8f00ff)";
        }
        if (colors.length === 1) {
            return colors[0].colorCode || "#ccc";
        }
        const step = 360 / colors.length;
        const gradientParts = colors.map((item, idx) => {
            const start = idx * step;
            const end = (idx + 1) * step;
            return `${item.colorCode || "#ccc"} ${start}deg ${end}deg`;
        });
        return `conic-gradient(${gradientParts.join(", ")})`;
    };

    // Load Categories & Color Palettes
    useEffect(() => {
        GetCategoryList({ filter_by_user: true })
            .then((res) => {
                const list = res?.list || res?.data?.list || [];
                setCategoryList(list);
            })
            .catch((err) => console.error("Error loading categories:", err));

        getColorPalettes()
            .then((res) => {
                if (res?.data) setColorPalettes(res.data);
            })
            .catch((err) => console.error("Error loading color palettes:", err));
    }, []);

    useEffect(() => {
        if (streamMeta) {
            const clicks = streamMeta.remaining_ai_clicks ?? streamMeta.seller?.remaining_ai_clicks ?? 20;
            setBalanceData({ remaining_ai_clicks: clicks });
        }
    }, [streamMeta]);

    const handleCategoryChange = (catId) => {
        addFormik.setFieldValue("categoryId", catId);
        addFormik.setFieldTouched("categoryId", true, true);
        addFormik.setFieldValue("subcategoryId", "");
        addFormik.setFieldValue("preSubCategoryId", "");
        addFormik.setFieldValue("attributes", []);
        setSubCategoryList([]);
        setPreSubCategoryList([]);
        setAddDynamicAttributes([]);

        if (catId) {
            setIsSubCategoryLoading(true);
            GetSubCategoryList({ categoryId: catId })
                .then((res) => {
                    const list = res?.list || res?.data?.list || [];
                    setSubCategoryList(list);
                })
                .catch((err) => console.error("Error loading subcategories:", err))
                .finally(() => setIsSubCategoryLoading(false));
        }
    };

    const handleSubCategoryChange = (subId) => {
        addFormik.setFieldValue("subcategoryId", subId);
        addFormik.setFieldValue("preSubCategoryId", "");
        addFormik.setFieldValue("attributes", []);
        setPreSubCategoryList([]);
        setAddDynamicAttributes([]);

        if (subId) {
            setIsPreSubCategoryLoading(true);
            GetPreSubCategoryList({ subcategoryId: subId })
                .then((res) => {
                    const list = res?.list || res?.data?.list || [];
                    setPreSubCategoryList(list);
                })
                .catch((err) => console.error("Error loading pre-subcategories:", err))
                .finally(() => setIsPreSubCategoryLoading(false));
        }
    };

    const handleAddPreSubCategoryChange = (val) => {
        addFormik.setFieldValue("preSubCategoryId", val);
        addFormik.setFieldTouched("preSubCategoryId", true, true);
        if (val) {
            setIsAddAttributeLoading(true);
            GetPreSubCategoryDetails(val)
                .then((res) => {
                    const responseData = res?.data || res;
                    const attributes = responseData?.data?.attributes || responseData?.attributes;
                    if (attributes && Array.isArray(attributes)) {
                        setAddDynamicAttributes(attributes);
                        const currentFormAttrs = Array.isArray(addFormik.values.attributes) ? [...addFormik.values.attributes] : [];
                        const updatedAttrs = attributes.map(attr => {
                            const existing = currentFormAttrs.find(a => a && a.name === attr.name);
                            return {
                                name: attr.name,
                                value: existing ? existing.value : (attr.isMultiSelect || attr.attributeType === "checkbox" ? [] : "")
                            };
                        });
                        addFormik.setFieldValue("attributes", updatedAttrs);
                    } else {
                        setAddDynamicAttributes([]);
                    }
                })
                .catch((err) => {
                    console.error("Attribute fetch error:", err);
                    setAddDynamicAttributes([]);
                })
                .finally(() => setIsAddAttributeLoading(false));
        } else {
            setAddDynamicAttributes([]);
            addFormik.setFieldValue("attributes", []);
        }
    };

    const addValidationSchema = Yup.object({
        title: Yup.string()
            .trim()
            .required("Title is required")
            .min(2, "Title must be at least 2 characters"),

        price: Yup.mixed().when("isAskForPrice", {
            is: false,
            then: () => Yup.number()
                .required("Price is required")
                .typeError("Price must be a valid number")
                .positive("Price must be positive")
                .max(10000000, "Price too large"),
            otherwise: () => Yup.mixed().notRequired(),
        }),

        description: Yup.string().trim(),

        picture: Yup.mixed().required("Product picture is required"),

        colors: Yup.array()
            .min(1, "At least one color is required")
            .required("Color is required"),

        categoryId: Yup.string()
            .trim()
            .required("Category is required"),

        subcategoryId: Yup.string()
            .trim()
            .required("Sub category is required"),

        preSubCategoryId: Yup.string()
            .trim()
            .required("Pre-sub category is required"),
    });

    const addFormik = useFormik({
        initialValues: {
            title: "",
            description: "",
            price: "",
            isAskForPrice: false,
            colors: [],
            picture: null,
            categoryId: "",
            subcategoryId: "",
            preSubCategoryId: "",
            attributes: [],
            useAI: true,
        },
        validationSchema: addValidationSchema,
        onSubmit: () => {}
    });

    const handleOpenAddModal = () => {
        setIsAddModalOpen(true);
        addFormik.resetForm();
        setAddImagePreview(null);
        setColorPickerTarget("add");
        setIsColorPickerOpen(false);
        setAddDynamicAttributes([]);
    };

    const handleCloseAddModal = () => {
        setIsAddModalOpen(false);
        addFormik.resetForm();
        setAddImagePreview(null);
        setIsColorPickerOpen(false);
        setAddDynamicAttributes([]);
    };

    const handleAddImageChange = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        addFormik.setFieldValue("picture", file);

        const reader = new FileReader();
        reader.onloadend = async () => {
            setAddImagePreview(reader.result);

            if (reader.result && addFormik.values.useAI) {
                const clicks = balanceData?.remaining_ai_clicks ?? 0;
                if (clicks <= 0) {
                    Swal.fire({
                        icon: "warning",
                        title: "Insufficient AI Clicks",
                        text: "You have 0 AI clicks remaining.",
                    });
                    addFormik.setFieldValue("useAI", false);
                    return;
                }

                setIsAnalyzing(true);
                try {
                    const resp = await AnalyzeProductImage(reader.result);
                    if (resp && resp.success && resp.data) {
                        if (!addFormik.values.title && resp.data.title) {
                            addFormik.setFieldValue("title", resp.data.title);
                        }
                        if (!addFormik.values.description && resp.data.description) {
                            addFormik.setFieldValue("description", resp.data.description);
                        }
                        if (resp.data.remaining_ai_clicks !== undefined) {
                            setBalanceData(prev => ({ ...prev, remaining_ai_clicks: resp.data.remaining_ai_clicks }));
                        }
                    } else if (resp && resp.data && resp.data.remaining_ai_clicks !== undefined) {
                        setBalanceData(prev => ({ ...prev, remaining_ai_clicks: resp.data.remaining_ai_clicks }));
                    }
                } catch (err) {
                    console.error("AI analysis error:", err);
                    if (err?.response?.data?.data?.remaining_ai_clicks !== undefined) {
                        setBalanceData(prev => ({ ...prev, remaining_ai_clicks: err.response.data.data.remaining_ai_clicks }));
                    }
                } finally {
                    setIsAnalyzing(false);
                }
            }
        };
        reader.readAsDataURL(file);
    };

    const handleSaveProduct = async (isPublished) => {
        const errors = await addFormik.validateForm();
        addFormik.setTouched({
            title: true,
            price: true,
            picture: true,
            colors: true,
            categoryId: true,
            subcategoryId: true,
            preSubCategoryId: true,
        });

        const hasColorError = !addFormik.values.colors || addFormik.values.colors.length === 0;
        const hasCategoryError = !addFormik.values.categoryId;
        const hasSubCategoryError = !addFormik.values.subcategoryId;
        const hasPreSubCategoryError = !addFormik.values.preSubCategoryId;

        if (Object.keys(errors).length > 0 || hasColorError || hasCategoryError || hasSubCategoryError || hasPreSubCategoryError) {
            if (hasColorError) {
                addFormik.setFieldError("colors", "At least one color is required");
            }
            if (hasCategoryError) {
                addFormik.setFieldError("categoryId", "Category is required");
            }
            if (hasSubCategoryError) {
                addFormik.setFieldError("subcategoryId", "Sub category is required");
            }
            if (hasPreSubCategoryError) {
                addFormik.setFieldError("preSubCategoryId", "Pre-sub category is required");
            }
            return;
        }

        try {
            setIsSubmittingAdd(true);
            const formData = new FormData();
            formData.append("stream_id", streamId);
            formData.append("title", addFormik.values.title);
            formData.append("description", addFormik.values.description || "");
            formData.append("isAskForPrice", addFormik.values.isAskForPrice ? "true" : "false");
            formData.append("price", addFormik.values.isAskForPrice ? "0" : (addFormik.values.price || "0"));
            if (addFormik.values.colors && addFormik.values.colors.length > 0) {
                formData.append("colors", JSON.stringify(addFormik.values.colors));
            }
            if (addFormik.values.categoryId) {
                formData.append("categoryId", addFormik.values.categoryId);
            }
            if (addFormik.values.subcategoryId) {
                formData.append("subcategoryId", addFormik.values.subcategoryId);
            }
            if (addFormik.values.preSubCategoryId) {
                formData.append("preSubCategoryId", addFormik.values.preSubCategoryId);
            }

            const attributesObj = {};
            if (addFormik.values.colors && addFormik.values.colors.length > 0) {
                attributesObj["Colors"] = addFormik.values.colors;
            }
            if (Array.isArray(addFormik.values.attributes)) {
                addFormik.values.attributes.forEach(attr => {
                    if (attr && attr.name && attr.value !== undefined && attr.value !== "" && (!Array.isArray(attr.value) || attr.value.length > 0)) {
                        attributesObj[attr.name] = attr.value;
                    }
                });
            }
            if (Object.keys(attributesObj).length > 0) {
                formData.append("attributes", JSON.stringify(attributesObj));
            }
            formData.append("isPublished", isPublished ? "true" : "false");
            if (addFormik.values.picture) {
                formData.append("image", addFormik.values.picture);
            }

            const resp = await addProductToStream(formData);
            if (resp && resp.success) {
                handleCloseAddModal();
                await fetchInitialProducts(false);
                setActiveTab(isPublished ? "accepted" : "not_accepted");
                Swal.fire({
                    icon: "success",
                    title: isPublished ? "Product Published" : "Product Saved",
                    text: isPublished ? "Product published to live stream viewers!" : "Product saved to AI Scans list.",
                    timer: 2000,
                    showConfirmButton: false,
                });
            } else {
                Swal.fire({
                    icon: "error",
                    text: resp?.message || "Failed to add product to stream",
                });
            }
        } catch (err) {
            console.error("Add product error:", err);
            Swal.fire({
                icon: "error",
                text: err?.response?.data?.message || err.message || "Failed to add product",
            });
        } finally {
            setIsSubmittingAdd(false);
        }
    };

    const handleAddExistingToStream = async (productId) => {
        try {
            const resp = await addProductToStream({
                stream_id: streamId,
                product_id: productId
            });
            if (resp.success) {
                await fetchInitialProducts(false);
                setActiveTab("not_accepted");
                Swal.fire({
                    icon: "success",
                    title: "Product Added",
                    text: "Product added to stream in AI Scans list.",
                    timer: 1500,
                    showConfirmButton: false,
                });
            } else {
                Swal.fire({
                    icon: "error",
                    text: resp.message || "Failed to add product to stream",
                });
            }
        } catch (err) {
            console.error("Add existing product error:", err);
            Swal.fire({
                icon: "error",
                text: err?.response?.data?.message || "Failed to add product",
            });
        }
    };
    useEffect(() => {
        fetchInitialProducts();

        if (isPast) return;

        const handleProducts = () => {
            fetchInitialProducts(false);
        };

        const handleNewProduct = () => {
            fetchInitialProducts(false);
        };

        const handleProductRemoved = () => {
            fetchInitialProducts(false);
        };

        SOCKET_CLIENT.on(SOCKET_EVENTS.STREAM_PRODUCTS_GENERATED, handleProducts);
        SOCKET_CLIENT.on(SOCKET_EVENTS.PRODUCT_REMOVED, handleProductRemoved);
        SOCKET_CLIENT.on(SOCKET_EVENTS.NEW_PRODUCT, handleNewProduct);
        SOCKET_CLIENT.on(SOCKET_EVENTS.AI_CLICKS_UPDATED, (data) => {
            console.log("AI_CLICKS_UPDATED received:", data);
            setStreamMeta(prev => {
                if (!prev) return prev;
                return {
                    ...prev,
                    remaining_ai_clicks: data.remaining_ai_clicks,
                    seller: {
                        ...(prev.seller || {}),
                        remaining_ai_clicks: data.remaining_ai_clicks,
                        total_ai_clicks: data.total_ai_clicks,
                        used_ai_clicks: data.used_ai_clicks
                    }
                };
            });
        });

        if (!SOCKET_CLIENT.connected) {
            SOCKET_CLIENT.connect();
        }

        SOCKET_CLIENT.emit(SOCKET_EVENTS.JOIN_STREAM, {
            streamID: streamId,
            userId: userId,
            role: ROLE.ADVERTISER,
            isAdvertiser: true
        });

        SOCKET_CLIENT.on(SOCKET_EVENTS.STREAM_VIEWER_COUNT, (data) => {
            const viewsCount = data?.totalDbViews !== undefined ? data.totalDbViews : (data?.totalViews !== undefined ? data.totalViews : data?.totalViewers);
            if (viewsCount !== undefined) setTotalViewers(viewsCount);
        });

        SOCKET_CLIENT.on(SOCKET_EVENTS.STREAM_ENDED_USER, (data) => {
            if (data?.isStreamCompleted) {
                Swal.fire({
                    icon: "info",
                    title: "Stream Ended",
                    text: "The publisher has ended this stream.",
                    confirmButtonText: "Go to Home"
                }).then(() => {
                    navigate("/");
                });
            }
        });

        return () => {
            SOCKET_CLIENT.off(SOCKET_EVENTS.STREAM_PRODUCTS_GENERATED, handleProducts);
            SOCKET_CLIENT.off(SOCKET_EVENTS.PRODUCT_REMOVED, handleProductRemoved);
            SOCKET_CLIENT.off(SOCKET_EVENTS.NEW_PRODUCT, handleNewProduct);
            SOCKET_CLIENT.off(SOCKET_EVENTS.AI_CLICKS_UPDATED);
            SOCKET_CLIENT.off(SOCKET_EVENTS.STREAM_VIEWER_COUNT);

            SOCKET_CLIENT.emit(SOCKET_EVENTS.LEAVE_STREAM, {
                streamID: streamId,
                userId: userId,
                isAdvertiser: true
            });
        };

    }, [streamId, userId, navigate]);
    useEffect(() => {
        const onBroadcast = (broadcast) => {
            if (broadcast?.event === SOCKET_EVENTS.STREAM_ENDED && broadcast?.data) {
                const endedStreamId = broadcast.data._id || broadcast.data.streamId;
                if (endedStreamId && String(endedStreamId) === String(streamId)) {
                    Swal.fire({
                        icon: "info",
                        title: "Stream Ended",
                        text: "The publisher has ended this stream.",
                        confirmButtonText: "Go to Home"
                    }).then(() => {
                        navigate("/");
                    });
                }
            }
        };

        SOCKET_CLIENT.on(SOCKET_EVENTS.GET_BROADCAST, onBroadcast);

        return () => {
            SOCKET_CLIENT.off(SOCKET_EVENTS.GET_BROADCAST, onBroadcast);
        };

    }, [streamId, navigate]);
    const handleVerifyProduct = async (productOrId) => {
        let product = typeof productOrId === "object" ? productOrId : null;
        if (!product) {
            const allLists = [
                ...(productsByTab.not_accepted || []),
                ...(productsByTab.accepted || []),
                ...(productsByTab.existing || []),
            ];
            product = allLists.find(p => String(p._id) === String(productOrId));
        }

        const catId = product?.categoryId?._id 
            ? String(product.categoryId._id) 
            : (product?.categoryId 
                ? String(product.categoryId) 
                : (product?.category?._id 
                    ? String(product.category._id) 
                    : ""));
        const subId = product?.subcategoryId?._id 
            ? String(product.subcategoryId._id) 
            : (product?.subcategoryId 
                ? String(product.subcategoryId) 
                : "");
        const preSubId = product?.preSubCategoryId?._id 
            ? String(product.preSubCategoryId._id) 
            : (product?.preSubCategoryId 
                ? String(product.preSubCategoryId) 
                : "");
        const hasColors = Array.isArray(product?.colors) && product.colors.length > 0;

        const isMissingFields = !catId || catId.trim() === "" ||
            !subId || subId.trim() === "" ||
            !preSubId || preSubId.trim() === "" ||
            !hasColors;

        if (isMissingFields) {
            Swal.fire({
                icon: 'warning',
                title: 'Mandatory Fields Required',
                text: 'Please fill in Color, Category, Sub Category, and Pre-Sub Category before publishing to stream.',
                toast: true,
                position: 'top-end',
                timer: 3500,
                showConfirmButton: false
            });
            if (product) {
                handleEditProduct(product);
                setTimeout(() => {
                    if (!hasColors) {
                        formik.setFieldTouched("colors", true, true);
                        formik.setFieldError("colors", "At least one color is required");
                    }
                    if (!catId) {
                        formik.setFieldTouched("categoryId", true, true);
                        formik.setFieldError("categoryId", "Category is required");
                    }
                    if (!subId) {
                        formik.setFieldTouched("subcategoryId", true, true);
                        formik.setFieldError("subcategoryId", "Sub category is required");
                    }
                    if (!preSubId) {
                        formik.setFieldTouched("preSubCategoryId", true, true);
                        formik.setFieldError("preSubCategoryId", "Pre-sub category is required");
                    }
                }, 100);
            }
            return;
        }

        const productId = product?._id || productOrId;
        try {
            const resp = await acceptProduct(productId, streamId);
            if (resp.success) {
                await fetchInitialProducts(false);
            }
            Swal.fire({
                icon: 'success',
                title: 'Verified',
                text: 'Product successfully verified and sent to stream!',
                timer: 1500,
                showConfirmButton: false
            });
        } catch (error) {
            const msg = error?.response?.data?.message?.toLowerCase() || "";
            if (msg.includes("category") || msg.includes("color") || msg.includes("mandatory")) {
                if (product) {
                    handleEditProduct(product);
                    setTimeout(() => {
                        formik.setFieldTouched("colors", true, true);
                        formik.setFieldTouched("categoryId", true, true);
                        formik.setFieldTouched("subcategoryId", true, true);
                        formik.setFieldTouched("preSubCategoryId", true, true);
                    }, 100);
                }
            }
            Swal.fire({
                icon: 'error',
                text: error?.response?.data?.message || 'Failed to verify product'
            });
        }
    };

    const handleUndoAccept = async (product) => {
        try {
            const undoId = product._id;
            const resp = await undoAcceptProduct(undoId, streamId);
            if (resp.success) {
                await fetchInitialProducts(false);

                Swal.fire({
                    icon: 'success',
                    title: 'Undone',
                    text: 'Product moved back to verification list',
                    timer: 1500,
                    showConfirmButton: false
                });
            }
        } catch (error) {
            Swal.fire({
                icon: 'error',
                text: error?.response?.data?.message || 'Failed to undo acceptance'
            });
        }
    };
    const handleEditProduct = (product) => {
        setEditMode(true);
        setEditProductId(product._id);
        const isLive = Boolean(product.is_live_on_stream || (product.verified_by_staff && product.status === 1 && product.is_live_on_stream !== false));
        setEditProductIsLive(isLive);
        setIsEditSubCategoryLoading(false);
        setIsEditPreSubCategoryLoading(false);
        setEditSubCategoryList([]);
        setEditPreSubCategoryList([]);
        setColorPickerTarget("edit");

        const catId = product.categoryId?._id ? String(product.categoryId._id) : (product.categoryId ? String(product.categoryId) : (product.category?._id ? String(product.category._id) : ""));
        const subId = product.subcategoryId?._id ? String(product.subcategoryId._id) : (product.subcategoryId ? String(product.subcategoryId) : "");
        const preSubId = product.preSubCategoryId?._id ? String(product.preSubCategoryId._id) : (product.preSubCategoryId ? String(product.preSubCategoryId) : "");

        let initialAttributes = [];
        if (product.attributes) {
            if (Array.isArray(product.attributes)) {
                initialAttributes = product.attributes.filter(a => a && a.name && a.name.toLowerCase() !== "colors");
            } else if (typeof product.attributes === 'object') {
                initialAttributes = Object.entries(product.attributes)
                    .filter(([name]) => name.toLowerCase() !== "colors")
                    .map(([name, value]) => ({ name, value }));
            }
        }

        formik.resetForm();
        formik.setValues({
            title: product.title || "",
            description: product.description || "",
            price: product.isAskForPrice ? "" : (product.price || ""),
            isAskForPrice: Boolean(product.isAskForPrice),
            colors: Array.isArray(product.colors) ? product.colors : [],
            picture: null,
            categoryId: catId,
            subcategoryId: subId,
            preSubCategoryId: preSubId,
            attributes: initialAttributes,
            useAI: true,
        });

        setExistingImage(product.image);
        setImagePreview(product.image);
        setEditDynamicAttributes([]);

        if (catId) {
            setIsEditSubCategoryLoading(true);
            GetSubCategoryList({ categoryId: catId })
                .then((res) => {
                    const list = res?.list || res?.data?.list || [];
                    setEditSubCategoryList(list);
                })
                .catch((err) => console.error("Error loading subcategories:", err))
                .finally(() => setIsEditSubCategoryLoading(false));
        }

        if (subId) {
            setIsEditPreSubCategoryLoading(true);
            GetPreSubCategoryList({ subcategoryId: subId })
                .then((res) => {
                    const list = res?.list || res?.data?.list || [];
                    setEditPreSubCategoryList(list);
                })
                .catch((err) => console.error("Error loading pre-subcategories:", err))
                .finally(() => setIsEditPreSubCategoryLoading(false));
        }

        if (preSubId) {
            setIsEditAttributeLoading(true);
            GetPreSubCategoryDetails(preSubId)
                .then((res) => {
                    const responseData = res?.data || res;
                    const attrs = responseData?.data?.attributes || responseData?.attributes;
                    if (attrs && Array.isArray(attrs)) {
                        setEditDynamicAttributes(attrs);
                    }
                })
                .catch((err) => console.error("Error loading pre-sub attributes:", err))
                .finally(() => setIsEditAttributeLoading(false));
        }

        setIsEditModalOpen(true);
    };
    const handleDeleteProduct = async (productId) => {
        try {
            await deleteLiveStreamProduct(productId);
            await fetchInitialProducts(false);
        } catch (error) {
            Swal.fire({
                icon: 'error',
                text: error?.response?.data?.message || 'Failed to delete product'
            });
        }
    };
    useEffect(() => {
        productCacheRef.current.clear();
    }, [streamId]);
    return (
        <div className="p-6 max-w-6xl mx-auto min-h-screen bg-gray-50/50">
            {isUpcoming && (
                <div className="w-full bg-gradient-to-r from-pink-50 via-purple-50 to-indigo-50 border border-pink-100 rounded-2xl p-6 mb-6 shadow-sm">
                    <div>
                        <span className="bg-[#FF63B5] text-white text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wider">
                            Upcoming Stream
                        </span>
                        <h2 className="text-xl font-bold text-gray-900 mt-2">{streamMeta?.title || "Scheduled Stream"}</h2>
                        <p className="text-sm text-gray-600 mt-1">
                            {streamMeta?.planned_date ? `Scheduled for: ${new Date(streamMeta.planned_date).toLocaleString()}` : "Stream is scheduled. Add and organize products before going live."}
                        </p>
                    </div>
                </div>
            )}

            {!isPast && !isUpcoming && (
                <div className="relative w-full rounded-2xl  overflow-hidden aspect-[16/9] md:aspect-auto min-h-[500px] md:min-h-[500px] bg-black mb-6 flex items-center justify-center">
                    <canvas ref={canvasRef} style={{ display: "none" }} />

                    {streamMeta?.isLive ? (
                        <>
                            <video ref={remoteVideoRef} autoPlay playsInline muted className="w-full h-[500px] object-contain" style={{ background: "#000" }} />

                            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-black/20 to-black/60 flex p-4 z-10 pointer-events-none">
                                <div className="absolute top-0 left-0 w-full h-[120px] bg-gradient-to-b from-black/60 to-transparent -z-1"></div>

                                <div className="absolute top-4 left-4 right-4 flex flex-col gap-2 z-20">
                                    <div className="flex items-center gap-2">
                                        <span className="bg-red-600 text-white text-[12px] px-3 py-1 rounded-full font-semibold pointer-events-auto">Live</span>
                                        <span className="bg-white/20 backdrop-blur-md border border-white/20 text-white text-[12px] px-3 py-1 rounded-full flex items-center gap-1 pointer-events-auto">
                                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-4 h-4">
                                                <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 0 1 0-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178Z" />
                                                <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
                                            </svg>
                                            {totalViewers || streamMeta?.listOfViewers?.length || 0} views
                                        </span>
                                    </div>
                                </div>
                                {streamMeta?.generate_products_by_ai === true && (
                                    <div className="absolute bottom-6 right-6 flex flex-col items-center gap-3 z-50 pointer-events-auto">
                                        <div className="bg-black/60 backdrop-blur-md px-3 py-1 rounded-full border border-white/20 shadow-lg">
                                            <span className="text-white text-[11px] font-bold whitespace-nowrap">
                                                AI Clicks: {streamMeta?.remaining_ai_clicks ?? streamMeta?.seller?.remaining_ai_clicks ?? 20}
                                            </span>
                                        </div>
                                        <button
                                            onClick={captureScreenshot}
                                            disabled={(streamMeta?.remaining_ai_clicks ?? streamMeta?.seller?.remaining_ai_clicks ?? 0) <= 0}
                                            className={`w-[50px] h-[50px] rounded-full flex items-center justify-center shadow-lg border transition-all ${
                                                (streamMeta?.seller?.remaining_ai_clicks || 0) <= 0
                                                    ? "bg-gray-500 opacity-50 cursor-not-allowed border-gray-400"
                                                    : "bg-white/20 backdrop-blur-md border-white/40 hover:bg-black/40 hover:scale-110 active:scale-95"
                                            }`}
                                            title={(streamMeta?.remaining_ai_clicks ?? streamMeta?.seller?.remaining_ai_clicks ?? 0) <= 0 ? "Insufficient AI Clicks" : "Take Screenshot"}
                                        >
                                            <Camera size={22} className="text-white" />
                                        </button>
                                    </div>
                                )}
                            </div>
                        </>
                    ) : (
                        <div className="text-white flex flex-col items-center justify-center">
                            <Camera size={48} className="text-white/30 mb-3" />
                            <span className="text-lg font-medium text-white/50">Live stream off</span>
                        </div>
                    )}
                </div>
            )}

            <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
                <div className="flex items-center gap-3">
                    <div onClick={() => navigate(-1)} className="cursor-pointer border border-gray-200 rounded-full w-fit px-4 py-2 bg-white hover:bg-gray-50 transition text-sm font-medium">Back</div>

                    {canReplyOnComments && (
                        <div onClick={() => setShowCommentsSidebar(true)} className="cursor-pointer border border-pink-200 bg-pink-50 text-pink-700 rounded-full w-fit px-4 py-2 hover:bg-pink-100 transition flex items-center gap-2 text-sm font-medium">
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M7.5 8.25h9m-9 3H12m-9.75 1.51c0 1.6 1.123 2.994 2.707 3.227 1.129.166 2.27.293 3.423.379.35.026.67.21.865.501L12 21l2.755-4.133a1.14 1.14 0 01.865-.501 48.172 48.172 0 003.423-.379c1.584-.233 2.707-1.626 2.707-3.228V6.741c0-1.602-1.123-2.995-2.707-3.228A48.394 48.394 0 0012 3c-2.392 0-4.744.175-7.043.513C3.373 3.746 2.25 5.14 2.25 6.741v6.018z" />
                            </svg>
                            Comments
                        </div>
                    )}
                </div>

                <button
                    type="button"
                    onClick={handleOpenAddModal}
                    className="cursor-pointer bg-[#FF63B5] hover:bg-[#e0489b] active:scale-95 text-white rounded-full px-5 py-2 flex items-center gap-2 text-sm font-medium transition shadow-sm border border-[#FF63B5]"
                >
                    <Plus className="w-4 h-4" />
                    <span>Add Product</span>
                </button>
            </div>
            <div className="flex flex-col md:flex-row items-start md:items-center gap-4 mb-6">
                <div className="flex-1">
                    <h1 className="text-xl flex flex-col items-start gap-2 font-bold text-gray-900 border-b border-gray-200 pb-3">
                        {isPast ? "Past Stream Products" : "Stream Products"}
                        <span className="text-xl text-gray-500 font-medium">{streamMeta?.title}</span>
                    </h1>
                    <p className="text-gray-500 mt-2 text-sm">
                        {isPast
                            ? "Products that were showcased during this past stream."
                            : isUpcoming
                            ? "Prepare and manage products for this upcoming stream."
                            : "Review incoming products and send them to the live audience."
                        }
                    </p>
                </div>
            </div>

            {/* Tabs */}
            {(() => {
                const TABS = [
                    { key: "not_accepted", label: "AI Scans", count: tabCounts.not_accepted || 0 },
                    { key: "accepted", label: "Active Live", count: tabCounts.accepted || 0 },
                    { key: "existing", label: "Catalog", count: tabCounts.existing || 0 },
                ];

                const currentTabProducts = productsByTab[activeTab] || [];

                return (
                    <>
                        <div className="flex gap-2 mb-8 border-b border-gray-200 overflow-x-auto no-scrollbar">
                            {TABS.map((tab) => (
                                <button
                                    key={tab.key}
                                    onClick={() => handleTabChange(tab.key)}
                                    className={`px-6 py-3 text-base font-semibold whitespace-nowrap border-b-2 transition-all duration-300 flex items-center gap-2 ${
                                        activeTab === tab.key
                                            ? "border-[var(--secondary-500)] text-[var(--secondary-500)]"
                                            : "border-transparent text-gray-500 hover:text-gray-700"
                                    }`}
                                >
                                    <span>{tab.label}</span>
                                    <span
                                        className={`text-xs px-2.5 py-0.5 rounded-full font-medium transition ${
                                            activeTab === tab.key
                                                ? "bg-[var(--secondary-500)] text-white"
                                                : "bg-gray-200 text-gray-600"
                                        }`}
                                    >
                                        {tab.count}
                                    </span>
                                </button>
                            ))}
                        </div>

                        {loading ? (
                            <div className="flex justify-center items-center py-20">
                                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-500"></div>
                            </div>
                        ) : currentTabProducts.length === 0 ? (
                            <div className="text-center py-20 bg-white shadow-sm border border-gray-100 rounded-xl">
                                <div className={`w-20 h-20 bg-blue-50 text-blue-500 rounded-full flex items-center justify-center mx-auto mb-4 ${!isPast && activeTab === "not_accepted" ? "animate-pulse" : ""}`}>
                                    <svg className="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 15l-2 5L9 9l11 4-5 2zm0 0l5 5M7.188 2.239l.777 2.897M5.136 7.965l-2.898-.777M13.95 2.05l-2.122 2.122m-5.657 5.656l-2.12 2.122"></path>
                                    </svg>
                                </div>
                                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                                    {activeTab === "accepted"
                                        ? "No Active Live Products"
                                        : activeTab === "not_accepted"
                                        ? (isPast ? "No AI Scanned Products" : "Waiting for Products...")
                                        : "No Catalog Products"}
                                </h3>
                                <p className="text-gray-500 max-w-sm mx-auto text-sm">
                                    {activeTab === "accepted"
                                        ? "No products are currently active live for this stream. Review and activate items from the AI Scans or Catalog tabs."
                                        : activeTab === "not_accepted"
                                        ? (isPast
                                            ? "No AI scanned products found for this stream."
                                            : "As the AI detects matching products or you scan items, they will appear here in real-time for your verification.")
                                        : "No catalog products found for this seller."}
                                </p>
                            </div>
                        ) : (
                            <>
                                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
                                    {currentTabProducts.map((product) => {
                                        const isLive = Boolean(product.is_live_on_stream || (product.verified_by_staff && product.status === 1 && product.is_live_on_stream !== false));
                                        return (
                                            <div
                                                key={product._id}
                                                className="bg-white rounded-xl shadow-sm border border-gray-100 flex flex-col overflow-hidden hover:shadow-md transition"
                                            >
                                                <div className="relative h-[220px] bg-gray-100">
                                                    {product.image ? (
                                                        <img
                                                            src={product.image || "/images/banner5.png"}
                                                            alt={product.title || "Product image"}
                                                            className="w-full h-full object-cover"
                                                        />
                                                    ) : (
                                                        <div className="w-full h-full flex items-center justify-center text-gray-400">
                                                            No Image
                                                        </div>
                                                    )}

                                                    <div className={`absolute top-2 right-2 text-xs font-bold px-2 py-1 rounded-md shadow-sm ${
                                                        isLive
                                                            ? 'bg-green-500 text-white'
                                                            : activeTab === 'existing'
                                                            ? 'bg-blue-50 text-blue-700 border border-blue-200'
                                                            : 'bg-yellow-400 text-yellow-900'
                                                    }`}>
                                                        {isLive ? 'Live on stream' : (activeTab === 'existing' ? 'In Catalog' : 'Needs Verification')}
                                                    </div>
                                                </div>

                                                <div className="p-3.5 sm:p-4 flex flex-col flex-1">
                                                    <h4 className="font-semibold text-gray-900 line-clamp-2 mb-1 text-sm sm:text-base">
                                                        {product.title || "Unnamed Product"}
                                                    </h4>

                                                    <p className="text-green-600 font-bold mb-3 text-sm">
                                                        {product.price ? `INR ${product.price}` : "No Price"}
                                                    </p>

                                                    <div className="mt-auto flex items-center gap-1.5 w-full pt-1">
                                                        {!isLive && canEdit && (
                                                            <button
                                                                onClick={() => handleEditProduct(product)}
                                                                className="w-8 h-8 flex items-center justify-center bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition flex-shrink-0 cursor-pointer shadow-xs"
                                                                title="Edit"
                                                            >
                                                                <Pencil className="w-3.5 h-3.5 text-gray-700" />
                                                            </button>
                                                        )}

                                                        {!isLive && canDelete && activeTab === "not_accepted" && (
                                                            <button
                                                                onClick={() => handleDeleteProduct(product._id)}
                                                                className="w-8 h-8 flex items-center justify-center bg-red-50 hover:bg-red-100 text-red-500 rounded-lg transition flex-shrink-0 cursor-pointer shadow-xs"
                                                                title="Delete"
                                                            >
                                                                <img src="/images/trash.svg" className="w-3.5 h-3.5 mx-auto text-red-500" alt="Delete" />
                                                            </button>
                                                        )}

                                                        {canAccept && (
                                                            <>
                                                                {isLive ? (
                                                                    <button
                                                                        onClick={() => handleUndoAccept(product)}
                                                                        className="flex-1 h-8 py-1 px-2.5 border border-red-500 text-red-600 hover:bg-red-50 rounded-lg text-xs font-semibold transition flex items-center justify-center gap-1.5 cursor-pointer whitespace-nowrap"
                                                                    >
                                                                        <X className="w-3.5 h-3.5 flex-shrink-0" />
                                                                        <span>Don't show on stream</span>
                                                                    </button>
                                                                ) : (
                                                                    <button
                                                                        onClick={() => handleVerifyProduct(product)}
                                                                        className="flex-1 h-8 px-2.5 bg-[var(--secondary-500)] hover:opacity-95 active:scale-98 text-white rounded-lg text-xs font-bold transition flex items-center justify-center gap-1.5 shadow-sm whitespace-nowrap cursor-pointer"
                                                                        title="Publish to Stream"
                                                                    >
                                                                        <Send className="w-3.5 h-3.5 flex-shrink-0" />
                                                                        <span className="whitespace-nowrap">Publish to Stream</span>
                                                                    </button>
                                                                )}
                                                            </>
                                                        )}

                                                        {!canAccept && !canEdit && (!canDelete || activeTab !== "not_accepted") && (
                                                            <p className="text-xs text-gray-400 italic w-full text-center py-2">No actions available</p>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>

                                {/* Sentinel target for Infinite Scroll */}
                                <div ref={observerTarget} className="h-10 flex items-center justify-center mt-6 w-full">
                                    {loadingMore && (
                                        <div className="flex items-center gap-2 text-gray-500 text-sm">
                                            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-[var(--secondary-500)]"></div>
                                            <span>Loading more products...</span>
                                        </div>
                                    )}
                                </div>
                            </>
                        )}
                    </>
                );
            })()}
            {/* Advertiser-Style Edit Product Modal */}
            {isEditModalOpen && (
                <div className="fixed inset-0 z-[9999] flex flex-col items-center justify-center p-4 overflow-y-auto">
                    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" onClick={handleCloseModal}></div>

                    <div className="relative bg-white rounded-2xl shadow-2xl max-w-[645px] w-full p-6 z-10 max-h-[85vh] flex flex-col overflow-hidden">
                        {/* Header */}
                        <div className="flex items-center justify-between pb-3 border-b border-gray-100 mb-4 flex-shrink-0">
                            <h2 className="text-xl font-bold text-gray-900">Edit Product</h2>
                            <button
                                type="button"
                                onClick={handleCloseModal}
                                className="p-1.5 rounded-full hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition cursor-pointer"
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        {/* Form Body */}
                        <div className="overflow-y-auto flex-1 pr-1 custom-scrollbar space-y-4">
                            {/* Picture Upload */}
                            <div className="form-group">
                                <div className="flex items-center justify-between mb-1.5">
                                    <label className="text-sm font-semibold text-gray-700 !mb-0">Picture *</label>
                                    <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full ${(balanceData?.remaining_ai_clicks ?? 0) > 0 ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}`}>
                                        {(balanceData?.remaining_ai_clicks ?? 0)} clicks remaining
                                    </span>
                                </div>

                                <div className="relative">
                                    <input
                                        id="edit-product-picture-input"
                                        type="file"
                                        accept="image/*,video/*"
                                        onChange={handleEditImageChange}
                                        className={`form-control-custom w-full cursor-pointer pr-10 ${formik.touched.picture && formik.errors.picture ? "border-red-500" : ""}`}
                                    />
                                    <button
                                        type="button"
                                        onClick={() => document.getElementById("edit-product-picture-input")?.click()}
                                        className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-600 cursor-pointer"
                                    >
                                        <img src="/images/upload.svg" alt="Upload" className="w-5 h-5" />
                                    </button>
                                </div>
                                {formik.touched.picture && formik.errors.picture && (
                                    <p className="text-red-500 text-xs mt-1">{formik.errors.picture}</p>
                                )}

                                {imagePreview && (
                                    <div className="mt-2 relative inline-block">
                                        <img
                                            src={imagePreview}
                                            alt="Product preview"
                                            className="w-20 h-20 object-cover rounded-lg border border-gray-200 shadow-sm"
                                        />
                                        <button
                                            type="button"
                                            onClick={() => {
                                                setImagePreview(null);
                                                setExistingImage(null);
                                                formik.setFieldValue("picture", null);
                                                const input = document.getElementById("edit-product-picture-input");
                                                if (input) input.value = "";
                                            }}
                                            className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 shadow hover:bg-red-600 transition cursor-pointer"
                                            title="Remove image"
                                        >
                                            <X className="w-3.5 h-3.5" />
                                        </button>
                                    </div>
                                )}

                                {isEditAnalyzing && (
                                    <div className="mt-2 flex items-center gap-2 text-sm text-indigo-600 font-medium">
                                        <div className="w-4 h-4 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin"></div>
                                        AI extracting details...
                                    </div>
                                )}
                            </div>

                            {/* AI Extraction Toggle */}
                            <div className="p-3.5 bg-indigo-50/80 rounded-xl border border-indigo-100 flex items-center justify-between">
                                <div>
                                    <p className="text-sm font-bold text-indigo-950">AI Data Extraction</p>
                                    <p className="text-[11px] text-indigo-700">Auto-fill title & description using AI</p>
                                </div>
                                <div className="flex items-center gap-2">
                                    <span className={`text-[11px] font-bold ${!formik.values.useAI ? "text-gray-900" : "text-gray-400"}`}>OFF</span>
                                    <input
                                        type="checkbox"
                                        className="toggle border-indigo-600 bg-gray-200 checked:border-indigo-600 checked:bg-indigo-600 cursor-pointer"
                                        checked={formik.values.useAI}
                                        onChange={(e) => formik.setFieldValue("useAI", e.target.checked)}
                                    />
                                    <span className={`text-[11px] font-bold ${formik.values.useAI ? "text-indigo-600" : "text-gray-400"}`}>ON</span>
                                </div>
                            </div>

                            {/* TITLE */}
                            <div className="form-group">
                                <label className="text-sm font-semibold text-gray-700 mb-1 block">Title *</label>
                                <input
                                    type="text"
                                    name="title"
                                    placeholder="Enter title"
                                    className={`form-control-custom w-full ${formik.touched.title && formik.errors.title ? "border-red-500" : ""}`}
                                    value={formik.values.title}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                />
                                {formik.touched.title && formik.errors.title && (
                                    <p className="text-red-500 text-xs mt-1">{formik.errors.title}</p>
                                )}
                            </div>

                            {/* DESCRIPTION */}
                            <div className="form-group">
                                <label className="text-sm font-semibold text-gray-700 mb-1 block">Description</label>
                                <textarea
                                    name="description"
                                    rows={3}
                                    placeholder="Enter description"
                                    className="form-control-custom w-full !h-[100px]"
                                    value={formik.values.description}
                                    onChange={formik.handleChange}
                                />
                            </div>

                            {/* COLORS */}
                            <div className="form-group">
                                <label className="text-sm font-semibold text-gray-700 mb-1 block">
                                    Colors * <span className="text-[10px] text-gray-400 font-normal">(Multi-select)</span>
                                </label>
                                <div>
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setColorPickerTarget("edit");
                                            setIsColorPickerOpen(true);
                                        }}
                                        className="flex items-center gap-2 px-3.5 py-2 rounded-xl border border-gray-200 hover:border-gray-300 bg-white text-xs font-bold text-gray-700 cursor-pointer transition shadow-sm"
                                        style={{
                                            borderColor: formik.touched.colors && formik.errors.colors
                                                ? "#ef4444"
                                                : formik.values.colors && formik.values.colors.length > 0 ? "#FF63B5" : "#e2e8f0"
                                        }}
                                    >
                                        <span
                                            className="w-4 h-4 rounded-full border border-gray-300 inline-block flex-shrink-0"
                                            style={{
                                                background: formik.values.colors && formik.values.colors.length > 0
                                                    ? getPalateBackground(formik.values.colors.map(c => ({ name: c, colorCode: getSwatchColorCode(c) })))
                                                    : "linear-gradient(135deg, #ff0000, #ff7f00, #ffff00, #00ff00, #0000ff, #4b0082, #8f00ff)"
                                            }}
                                        />
                                        <span>Select Colors</span>
                                        {formik.values.colors && formik.values.colors.length > 0 && (
                                            <span className="bg-[#FF63B5] text-white text-[9px] px-1.5 py-0.5 rounded-full">
                                                {formik.values.colors.length}
                                            </span>
                                        )}
                                    </button>
                                </div>
                                {formik.touched.colors && formik.errors.colors && (
                                    <p className="text-red-500 text-xs mt-1">{formik.errors.colors}</p>
                                )}

                                {formik.values.colors && formik.values.colors.length > 0 && (
                                    <div className="flex flex-wrap gap-1.5 mt-2">
                                        {formik.values.colors.map(v => (
                                            <span key={v} className="inline-flex items-center gap-1.5 bg-gray-100 text-gray-700 px-2.5 py-1 rounded-lg text-xs font-semibold">
                                                <span className="w-2.5 h-2.5 rounded-full border border-gray-300 inline-block" style={{ backgroundColor: getSwatchColorCode(v) }} />
                                                {v}
                                                <button
                                                    type="button"
                                                    onClick={() => formik.setFieldValue("colors", formik.values.colors.filter(x => x !== v))}
                                                    className="text-gray-400 hover:text-gray-600 font-bold ml-1 cursor-pointer"
                                                >
                                                    ✕
                                                </button>
                                            </span>
                                        ))}
                                    </div>
                                )}
                            </div>

                            {/* ASK FOR PRICE */}
                            <div className="form-group mb-2">
                                <label className="flex items-center gap-2 cursor-pointer">
                                    <input
                                        type="checkbox"
                                        name="isAskForPrice"
                                        className="w-4 h-4 accent-[#FF63B5]"
                                        checked={formik.values.isAskForPrice}
                                        onChange={formik.handleChange}
                                    />
                                    <span className="text-sm font-medium text-gray-700">Ask for price</span>
                                </label>
                            </div>

                            {/* PRICE */}
                            {!formik.values.isAskForPrice && (
                                <div className="form-group">
                                    <label className="text-sm font-semibold text-gray-700 mb-1 block">Price</label>
                                    <input
                                        type="number"
                                        name="price"
                                        min="1"
                                        max="9999999"
                                        step="0.01"
                                        placeholder="Enter price"
                                        className={`form-control-custom w-full ${formik.touched.price && formik.errors.price ? "border-red-500" : ""}`}
                                        value={formik.values.price}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                    />
                                    {formik.touched.price && formik.errors.price && (
                                        <p className="text-red-500 text-xs mt-1">{formik.errors.price}</p>
                                    )}
                                </div>
                            )}

                            {/* STREAM */}
                            <div className="form-group">
                                <label className="text-sm font-semibold text-gray-700 mb-1 block">Stream</label>
                                <select
                                    name="stream_id"
                                    className="form-control-custom w-full bg-gray-50 text-gray-600 cursor-not-allowed"
                                    value={streamId}
                                    disabled
                                >
                                    <option value={streamId}>{streamMeta?.title || "Current Stream"}</option>
                                </select>
                            </div>

                            {/* CATEGORY */}
                            {/* CATEGORY */}
                            <div className="form-group mb-4">
                                <label className="text-sm font-semibold text-gray-700 mb-1 block">Category *</label>
                                <CategorySelectDropdown
                                    value={formik.values.categoryId}
                                    onChange={(val) => handleEditCategoryChange(val)}
                                    options={categoryList}
                                    placeholder="Select Category"
                                    fallbackIcon="/category.png"
                                    showAllOption={false}
                                />
                                {formik.touched.categoryId && formik.errors.categoryId && (
                                    <p className="text-red-500 text-xs mt-1">{formik.errors.categoryId}</p>
                                )}
                            </div>

                            {/* SUB CATEGORIES */}
                            <div className="form-group mb-4">
                                <label className="text-sm font-semibold text-gray-700 mb-1 block">Sub Categories *</label>
                                <CategorySelectDropdown
                                    value={formik.values.subcategoryId}
                                    onChange={(val) => handleEditSubCategoryChange(val)}
                                    options={editSubCategoryList}
                                    placeholder={isEditSubCategoryLoading ? "Loading..." : "Select Sub Category"}
                                    disabled={!formik.values.categoryId || isEditSubCategoryLoading}
                                    fallbackIcon="/category.png"
                                    showAllOption={false}
                                />
                                {formik.touched.subcategoryId && formik.errors.subcategoryId && (
                                    <p className="text-red-500 text-xs mt-1">{formik.errors.subcategoryId}</p>
                                )}
                            </div>

                            {/* PRE-SUB CATEGORIES */}
                            <div className="form-group mb-4">
                                <label className="text-sm font-semibold text-gray-700 mb-1 block">Pre-Sub Categories *</label>
                                <CategorySelectDropdown
                                    value={formik.values.preSubCategoryId}
                                    onChange={(val) => handleEditPreSubCategoryChange(val)}
                                    options={editPreSubCategoryList}
                                    placeholder={isEditPreSubCategoryLoading ? "Loading..." : "Select Pre-Sub Category"}
                                    disabled={!formik.values.subcategoryId || isEditPreSubCategoryLoading}
                                    fallbackIcon="/category.png"
                                    showAllOption={false}
                                />
                                {formik.touched.preSubCategoryId && formik.errors.preSubCategoryId && (
                                    <p className="text-red-500 text-xs mt-1">{formik.errors.preSubCategoryId}</p>
                                )}
                            </div>

                            {/* DYNAMIC ATTRIBUTES */}
                            <ProductAttributesSection
                                attributes={editDynamicAttributes}
                                values={formik.values.attributes}
                                onChange={(updated) => formik.setFieldValue("attributes", updated)}
                                colorPalettes={colorPalettes}
                                isLoading={isEditAttributeLoading}
                            />

                            {/* Action Buttons */}
                            <div className="flex items-center gap-3 pt-4 border-t border-gray-100">
                                <button
                                    type="button"
                                    className="flex-1 flex justify-center items-center text-center !bg-white !text-[#FF63B5] border-2 !border-[#FF63B5] hover:!bg-[#FF63B5] hover:!text-white transition py-2.5 rounded-xl font-bold text-sm cursor-pointer shadow-sm"
                                    disabled={isSubmittingEdit}
                                    onClick={() => handleUpdateProduct(false)}
                                >
                                    {isSubmittingEdit ? "Updating..." : (editProductIsLive ? "Update Product" : "Save")}
                                </button>
                                {!editProductIsLive && (
                                    <button
                                        type="button"
                                        className="flex-1 flex justify-center items-center text-center !bg-[#FF7373] hover:!bg-[#ff5b5b] !text-white border-0 transition py-2.5 rounded-xl font-bold text-sm cursor-pointer shadow-sm"
                                        disabled={isSubmittingEdit}
                                        onClick={() => handleUpdateProduct(true)}
                                    >
                                        {isSubmittingEdit ? "Updating..." : "Save and Live"}
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Floating Close Button */}
                    <div className="flex justify-center mt-3 z-10 flex-shrink-0">
                        <button
                            type="button"
                            onClick={handleCloseModal}
                            className="bg-white shadow-lg border border-gray-200 flex items-center gap-1.5 px-4 py-2 rounded-full hover:bg-gray-50 transition cursor-pointer text-xs font-bold text-gray-700"
                        >
                            <img src="/images/close.svg" alt="close" className="w-3.5 h-3.5" />
                            <span>Close</span>
                        </button>
                    </div>
                </div>
            )}

            {/* Advertiser-Style Create Product Modal */}
            {isAddModalOpen && (
                <div className="fixed inset-0 z-[9999] flex flex-col items-center justify-center p-4 overflow-y-auto">
                    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" onClick={handleCloseAddModal}></div>

                    <div className="relative bg-white rounded-2xl shadow-2xl max-w-[645px] w-full p-6 z-10 max-h-[85vh] flex flex-col overflow-hidden">
                        {/* Header */}
                        <div className="flex items-center justify-between pb-3 border-b border-gray-100 mb-4 flex-shrink-0">
                            <h2 className="text-xl font-bold text-gray-900">Create Product</h2>
                            <button
                                type="button"
                                onClick={handleCloseAddModal}
                                className="p-1.5 rounded-full hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition cursor-pointer"
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        {/* Form Body */}
                        <div className="overflow-y-auto flex-1 pr-1 custom-scrollbar space-y-4">
                            {/* Picture Upload */}
                            <div className="form-group">
                                <div className="flex items-center justify-between mb-1.5">
                                    <label className="text-sm font-semibold text-gray-700 !mb-0">Picture *</label>
                                    <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full ${(balanceData?.remaining_ai_clicks ?? 0) > 0 ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}`}>
                                        {(balanceData?.remaining_ai_clicks ?? 0)} clicks remaining
                                    </span>
                                </div>

                                <div className="relative">
                                    <input
                                        id="add-product-picture-input"
                                        type="file"
                                        accept="image/*,video/*"
                                        onChange={handleAddImageChange}
                                        className={`form-control-custom w-full cursor-pointer pr-10 ${addFormik.touched.picture && addFormik.errors.picture ? "border-red-500" : ""}`}
                                    />
                                    <button
                                        type="button"
                                        onClick={() => document.getElementById("add-product-picture-input")?.click()}
                                        className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-600 cursor-pointer"
                                    >
                                        <img src="/images/upload.svg" alt="Upload" className="w-5 h-5" />
                                    </button>
                                </div>
                                {addFormik.touched.picture && addFormik.errors.picture && (
                                    <p className="text-red-500 text-xs mt-1">{addFormik.errors.picture}</p>
                                )}

                                {addImagePreview && (
                                    <div className="mt-2 relative inline-block">
                                        <img
                                            src={addImagePreview}
                                            alt="Product preview"
                                            className="w-20 h-20 object-cover rounded-lg border border-gray-200 shadow-sm"
                                        />
                                        <button
                                            type="button"
                                            onClick={() => {
                                                setAddImagePreview(null);
                                                addFormik.setFieldValue("picture", null);
                                                const input = document.getElementById("add-product-picture-input");
                                                if (input) input.value = "";
                                            }}
                                            className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 shadow hover:bg-red-600 transition cursor-pointer"
                                            title="Remove image"
                                        >
                                            <X className="w-3.5 h-3.5" />
                                        </button>
                                    </div>
                                )}

                                {isAnalyzing && (
                                    <div className="mt-2 flex items-center gap-2 text-sm text-indigo-600 font-medium">
                                        <div className="w-4 h-4 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin"></div>
                                        AI extracting details...
                                    </div>
                                )}
                            </div>

                            {/* AI Extraction Toggle */}
                            <div className="p-3.5 bg-indigo-50/80 rounded-xl border border-indigo-100 flex items-center justify-between">
                                <div>
                                    <p className="text-sm font-bold text-indigo-950">AI Data Extraction</p>
                                    <p className="text-[11px] text-indigo-700">Auto-fill title & description using AI</p>
                                </div>
                                <div className="flex items-center gap-2">
                                    <span className={`text-[11px] font-bold ${!addFormik.values.useAI ? "text-gray-900" : "text-gray-400"}`}>OFF</span>
                                    <input
                                        type="checkbox"
                                        className="toggle border-indigo-600 bg-gray-200 checked:border-indigo-600 checked:bg-indigo-600 cursor-pointer"
                                        checked={addFormik.values.useAI}
                                        onChange={(e) => addFormik.setFieldValue("useAI", e.target.checked)}
                                    />
                                    <span className={`text-[11px] font-bold ${addFormik.values.useAI ? "text-indigo-600" : "text-gray-400"}`}>ON</span>
                                </div>
                            </div>

                            {/* TITLE */}
                            <div className="form-group">
                                <label className="text-sm font-semibold text-gray-700 mb-1 block">Title *</label>
                                <input
                                    type="text"
                                    name="title"
                                    placeholder="Enter title"
                                    className={`form-control-custom w-full ${addFormik.touched.title && addFormik.errors.title ? "border-red-500" : ""}`}
                                    value={addFormik.values.title}
                                    onChange={addFormik.handleChange}
                                    onBlur={addFormik.handleBlur}
                                />
                                {addFormik.touched.title && addFormik.errors.title && (
                                    <p className="text-red-500 text-xs mt-1">{addFormik.errors.title}</p>
                                )}
                            </div>

                            {/* DESCRIPTION */}
                            <div className="form-group">
                                <label className="text-sm font-semibold text-gray-700 mb-1 block">Description</label>
                                <textarea
                                    name="description"
                                    rows={3}
                                    placeholder="Enter description"
                                    className="form-control-custom w-full !h-[100px]"
                                    value={addFormik.values.description}
                                    onChange={addFormik.handleChange}
                                />
                            </div>

                            {/* COLORS */}
                            <div className="form-group">
                                <label className="text-sm font-semibold text-gray-700 mb-1 block">
                                    Colors * <span className="text-[10px] text-gray-400 font-normal">(Multi-select)</span>
                                </label>
                                <div>
                                    <button
                                        type="button"
                                        onClick={() => setIsColorPickerOpen(true)}
                                        className="flex items-center gap-2 px-3.5 py-2 rounded-xl border border-gray-200 hover:border-gray-300 bg-white text-xs font-bold text-gray-700 cursor-pointer transition shadow-sm"
                                        style={{
                                            borderColor: addFormik.touched.colors && addFormik.errors.colors
                                                ? "#ef4444"
                                                : addFormik.values.colors && addFormik.values.colors.length > 0 ? "#FF63B5" : "#e2e8f0"
                                        }}
                                    >
                                        <span
                                            className="w-4 h-4 rounded-full border border-gray-300 inline-block flex-shrink-0"
                                            style={{
                                                background: addFormik.values.colors && addFormik.values.colors.length > 0
                                                    ? getPalateBackground(addFormik.values.colors.map(c => ({ name: c, colorCode: getSwatchColorCode(c) })))
                                                    : "linear-gradient(135deg, #ff0000, #ff7f00, #ffff00, #00ff00, #0000ff, #4b0082, #8f00ff)"
                                            }}
                                        />
                                        <span>Select Colors</span>
                                        {addFormik.values.colors && addFormik.values.colors.length > 0 && (
                                            <span className="bg-[#FF63B5] text-white text-[9px] px-1.5 py-0.5 rounded-full">
                                                {addFormik.values.colors.length}
                                            </span>
                                        )}
                                    </button>
                                </div>
                                {addFormik.touched.colors && addFormik.errors.colors && (
                                    <p className="text-red-500 text-xs mt-1">{addFormik.errors.colors}</p>
                                )}

                                {addFormik.values.colors && addFormik.values.colors.length > 0 && (
                                    <div className="flex flex-wrap gap-1.5 mt-2">
                                        {addFormik.values.colors.map(v => (
                                            <span key={v} className="inline-flex items-center gap-1.5 bg-gray-100 text-gray-700 px-2.5 py-1 rounded-lg text-xs font-semibold">
                                                <span className="w-2.5 h-2.5 rounded-full border border-gray-300 inline-block" style={{ backgroundColor: getSwatchColorCode(v) }} />
                                                {v}
                                                <button
                                                    type="button"
                                                    onClick={() => addFormik.setFieldValue("colors", addFormik.values.colors.filter(x => x !== v))}
                                                    className="text-gray-400 hover:text-gray-600 font-bold ml-1 cursor-pointer"
                                                >
                                                    ✕
                                                </button>
                                            </span>
                                        ))}
                                    </div>
                                )}
                            </div>

                            {/* ASK FOR PRICE */}
                            <div className="form-group mb-2">
                                <label className="flex items-center gap-2 cursor-pointer">
                                    <input
                                        type="checkbox"
                                        name="isAskForPrice"
                                        className="w-4 h-4 accent-[#FF63B5]"
                                        checked={addFormik.values.isAskForPrice}
                                        onChange={addFormik.handleChange}
                                    />
                                    <span className="text-sm font-medium text-gray-700">Ask for price</span>
                                </label>
                            </div>

                            {/* PRICE */}
                            {!addFormik.values.isAskForPrice && (
                                <div className="form-group">
                                    <label className="text-sm font-semibold text-gray-700 mb-1 block">Price</label>
                                    <input
                                        type="number"
                                        name="price"
                                        min="1"
                                        max="9999999"
                                        step="0.01"
                                        placeholder="Enter price"
                                        className={`form-control-custom w-full ${addFormik.touched.price && addFormik.errors.price ? "border-red-500" : ""}`}
                                        value={addFormik.values.price}
                                        onChange={addFormik.handleChange}
                                        onBlur={addFormik.handleBlur}
                                    />
                                    {addFormik.touched.price && addFormik.errors.price && (
                                        <p className="text-red-500 text-xs mt-1">{addFormik.errors.price}</p>
                                    )}
                                </div>
                            )}

                            {/* STREAM */}
                            <div className="form-group">
                                <label className="text-sm font-semibold text-gray-700 mb-1 block">Stream</label>
                                <select
                                    name="stream_id"
                                    className="form-control-custom w-full bg-gray-50 text-gray-600 cursor-not-allowed"
                                    value={streamId}
                                    disabled
                                >
                                    <option value={streamId}>{streamMeta?.title || "Current Stream"}</option>
                                </select>
                            </div>

                            {/* CATEGORY */}
                            <div className="form-group mb-4">
                                <label className="text-sm font-semibold text-gray-700 mb-1 block">Category *</label>
                                <CategorySelectDropdown
                                    value={addFormik.values.categoryId}
                                    onChange={(val) => handleCategoryChange(val)}
                                    options={categoryList}
                                    placeholder="Select Category"
                                    fallbackIcon="/category.png"
                                    showAllOption={false}
                                />
                                {addFormik.touched.categoryId && addFormik.errors.categoryId && (
                                    <p className="text-red-500 text-xs mt-1">{addFormik.errors.categoryId}</p>
                                )}
                            </div>

                            {/* SUB CATEGORIES */}
                            <div className="form-group mb-4">
                                <label className="text-sm font-semibold text-gray-700 mb-1 block">Sub Categories *</label>
                                <CategorySelectDropdown
                                    value={addFormik.values.subcategoryId}
                                    onChange={(val) => handleSubCategoryChange(val)}
                                    options={subCategoryList}
                                    placeholder={isSubCategoryLoading ? "Loading..." : "Select Sub Category"}
                                    disabled={!addFormik.values.categoryId || isSubCategoryLoading}
                                    fallbackIcon="/category.png"
                                    showAllOption={false}
                                />
                                {addFormik.touched.subcategoryId && addFormik.errors.subcategoryId && (
                                    <p className="text-red-500 text-xs mt-1">{addFormik.errors.subcategoryId}</p>
                                )}
                            </div>

                            {/* PRE-SUB CATEGORIES */}
                            <div className="form-group mb-4">
                                <label className="text-sm font-semibold text-gray-700 mb-1 block">Pre-Sub Categories *</label>
                                <CategorySelectDropdown
                                    value={addFormik.values.preSubCategoryId}
                                    onChange={(val) => handleAddPreSubCategoryChange(val)}
                                    options={preSubCategoryList}
                                    placeholder={isPreSubCategoryLoading ? "Loading..." : "Select Pre-Sub Category"}
                                    disabled={!addFormik.values.subcategoryId || isPreSubCategoryLoading}
                                    fallbackIcon="/category.png"
                                    showAllOption={false}
                                />
                                {addFormik.touched.preSubCategoryId && addFormik.errors.preSubCategoryId && (
                                    <p className="text-red-500 text-xs mt-1">{addFormik.errors.preSubCategoryId}</p>
                                )}
                            </div>

                            {/* DYNAMIC ATTRIBUTES */}
                            <ProductAttributesSection
                                attributes={addDynamicAttributes}
                                values={addFormik.values.attributes}
                                onChange={(updated) => addFormik.setFieldValue("attributes", updated)}
                                colorPalettes={colorPalettes}
                                isLoading={isAddAttributeLoading}
                            />

                            {/* Action Buttons */}
                            <div className="flex items-center gap-3 pt-4 border-t border-gray-100">
                                <button
                                    type="button"
                                    className="flex-1 flex justify-center items-center text-center !bg-white !text-[#FF63B5] border-2 !border-[#FF63B5] hover:!bg-[#FF63B5] hover:!text-white transition py-2.5 rounded-xl font-bold text-sm cursor-pointer shadow-sm"
                                    disabled={isSubmittingAdd}
                                    onClick={() => handleSaveProduct(false)}
                                >
                                    {isSubmittingAdd ? "Saving..." : "Save"}
                                </button>
                                <button
                                    type="button"
                                    className="flex-1 flex justify-center items-center text-center !bg-[#FF7373] hover:!bg-[#ff5b5b] !text-white border-0 transition py-2.5 rounded-xl font-bold text-sm cursor-pointer shadow-sm"
                                    disabled={isSubmittingAdd}
                                    onClick={() => handleSaveProduct(true)}
                                >
                                    {isSubmittingAdd ? "Saving..." : "Save and Live"}
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Floating Close Button */}
                    <div className="flex justify-center mt-3 z-10 flex-shrink-0">
                        <button
                            type="button"
                            onClick={handleCloseAddModal}
                            className="bg-white shadow-lg border border-gray-200 flex items-center gap-1.5 px-4 py-2 rounded-full hover:bg-gray-50 transition cursor-pointer text-xs font-bold text-gray-700"
                        >
                            <img src="/images/close.svg" alt="close" className="w-3.5 h-3.5" />
                            <span>Close</span>
                        </button>
                    </div>
                </div>
            )}

            {/* Color Palette Modal */}
            {isColorPickerOpen && (
                <div className="fixed inset-0 z-[10000] flex items-center justify-center p-4 bg-black/50 backdrop-blur-xs" onClick={() => setIsColorPickerOpen(false)}>
                    <div className="bg-white rounded-2xl shadow-2xl max-w-[640px] w-full p-4 space-y-3 max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-between border-b border-gray-100 pb-2">
                            <span className="text-xs font-extrabold text-gray-700 uppercase tracking-wider">
                                Select Colors
                            </span>
                            <button
                                type="button"
                                onClick={() => setIsColorPickerOpen(false)}
                                className="text-gray-400 hover:text-gray-600 text-sm font-bold cursor-pointer"
                            >
                                ✕
                            </button>
                        </div>

                        <div className="pr-1">
                            <div className="grid gap-1 bg-gray-50 p-2 rounded-xl border border-gray-200" style={{ gridTemplateColumns: `repeat(${colorPalettes.length || 1}, 1fr)` }}>
                                {colorPalettes.map((col) => (
                                    <div
                                        key={col.name}
                                        style={{
                                            background: "#2c2c2c",
                                            color: "#ffffff",
                                            fontSize: "9px",
                                            fontWeight: "bold",
                                            textAlign: "center",
                                            padding: "4px 1px",
                                            borderRadius: "3px",
                                            overflow: "hidden",
                                            textOverflow: "ellipsis",
                                            whiteSpace: "nowrap",
                                            textTransform: "uppercase"
                                        }}
                                        title={col.name}
                                    >
                                        {col.name.split(" ")[0]}
                                    </div>
                                ))}

                                {Array.from({ length: Math.max(...colorPalettes.map(col => col.colors ? col.colors.length : 0), 1) }).map((_, rowIndex) => (
                                    <React.Fragment key={rowIndex}>
                                        {colorPalettes.map((col) => {
                                            const color = col.colors ? col.colors[rowIndex] : null;
                                            if (!color) {
                                                return <div key={`${col.name}-${rowIndex}`} />;
                                            }
                                            const targetFormik = colorPickerTarget === "edit" ? formik : addFormik;
                                            const currentColors = targetFormik.values.colors || [];
                                            const isColorSelected = currentColors.includes(color.name);

                                            let swatchBg = color.hex;
                                            if (color.name === "Transparent") {
                                                swatchBg = 'repeating-linear-gradient(45deg, #e0e0e0, #e0e0e0 4px, #ffffff 4px, #ffffff 8px)';
                                            } else if (color.name === "Multicolour") {
                                                swatchBg = 'linear-gradient(135deg, #ff0000, #ff7f00, #ffff00, #00ff00, #0000ff, #4b0082, #8f00ff)';
                                            } else if (color.gradient) {
                                                swatchBg = color.gradient;
                                            }

                                            return (
                                                <button
                                                    key={`${col.name}-${rowIndex}`}
                                                    type="button"
                                                    title={`${color.name} (${color.hex})`}
                                                    onClick={() => {
                                                        const current = targetFormik.values.colors || [];
                                                        if (isColorSelected) {
                                                            targetFormik.setFieldValue("colors", current.filter(v => v !== color.name));
                                                        } else {
                                                            targetFormik.setFieldValue("colors", [...current, color.name]);
                                                        }
                                                    }}
                                                    style={{
                                                        width: "100%",
                                                        height: "22px",
                                                        background: swatchBg,
                                                        border: isColorSelected ? "2.5px solid #FF63B5" : "1px solid rgba(0,0,0,0.15)",
                                                        borderRadius: "3px",
                                                        cursor: "pointer",
                                                        transition: "all 0.15s ease",
                                                        transform: isColorSelected ? "scale(1.05)" : "none"
                                                    }}
                                                />
                                            );
                                        })}
                                    </React.Fragment>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Sidebar Modal for Comments */}
            {showCommentsSidebar && (
                <div className="fixed inset-0 z-[100] flex justify-end">
                    <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setShowCommentsSidebar(false)}></div>
                    <div className="relative w-full max-w-md bg-white h-full shadow-2xl flex flex-col transform transition-transform duration-300 origin-right">
                        <StaffComments passedStreamId={streamId} isModal={true} onClose={() => setShowCommentsSidebar(false)} />
                    </div>
                </div>
            )}
        </div>
    );
}
